import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { TrendingUp, Flame, ExternalLink, MessageSquareText, RefreshCw } from "lucide-react";

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ROISignals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | hot

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roi-signals");
      setRows(data.signals || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "hot") return rows.filter((r) => r.high_intent_alert_sent);
    return rows;
  }, [rows, filter]);

  const stats = useMemo(() => {
    const totalSessions = rows.length;
    const hot = rows.filter((r) => r.high_intent_alert_sent).length;
    const totalInteractions = rows.reduce((s, r) => s + (r.interactions || 0), 0);
    const topUpside = rows.reduce((mx, r) => Math.max(mx, r.max_net_upside || 0), 0);
    return { totalSessions, hot, totalInteractions, topUpside };
  }, [rows]);

  return (
    <div className="p-8 max-w-7xl" data-testid="roi-signals-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 font-mono">Pricing intent</div>
          <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3">
            <TrendingUp className="w-7 h-7 text-amber-500" />
            ROI calculator signals
          </h1>
          <p className="text-neutral-600 mt-2 text-sm max-w-2xl">
            Every visitor who moves a slider on your public pricing page shows up here.
            When their computed net upside crosses the threshold, we fire a high-intent email alert (once per session).
          </p>
        </div>
        <button
          onClick={load}
          data-testid="roi-signals-refresh-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-200 text-sm hover:bg-neutral-50"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Sessions" value={stats.totalSessions} testId="roi-stat-sessions" />
        <Stat label="High-intent" value={stats.hot} accent="amber" testId="roi-stat-hot" />
        <Stat label="Interactions" value={stats.totalInteractions} testId="roi-stat-interactions" />
        <Stat label="Top net upside" value={fmt(stats.topUpside)} accent="green" testId="roi-stat-top-upside" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4 text-sm">
        <button
          onClick={() => setFilter("all")}
          data-testid="roi-filter-all"
          className={`px-3 py-1.5 border ${filter === "all" ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}
        >
          All ({rows.length})
        </button>
        <button
          onClick={() => setFilter("hot")}
          data-testid="roi-filter-hot"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 border ${filter === "hot" ? "border-amber-500 bg-amber-500 text-white" : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}
        >
          <Flame className="w-3.5 h-3.5" /> High-intent ({stats.hot})
        </button>
      </div>

      {/* Table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
            <tr>
              <th className="text-left px-4 py-3">Session</th>
              <th className="text-left px-4 py-3">Calls/wk</th>
              <th className="text-left px-4 py-3">Avg job</th>
              <th className="text-left px-4 py-3">Miss %</th>
              <th className="text-right px-4 py-3">Rev lost/mo</th>
              <th className="text-right px-4 py-3">Net upside</th>
              <th className="text-left px-4 py-3">Suggested</th>
              <th className="text-left px-4 py-3">Chat</th>
              <th className="text-right px-4 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-neutral-400" data-testid="roi-signals-empty">
                No {filter === "hot" ? "high-intent " : ""}sessions yet. Share your pricing page to start collecting signals.
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.session_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`roi-signal-row-${r.session_id.slice(0, 8)}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {r.high_intent_alert_sent && <Flame className="w-3.5 h-3.5 text-amber-500 shrink-0" title="High-intent alert fired" />}
                    <code className="text-xs bg-neutral-100 px-1.5 py-0.5 rounded">{r.session_id.slice(0, 10)}…</code>
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">{r.interactions || 0} interaction{r.interactions === 1 ? "" : "s"}</div>
                </td>
                <td className="px-4 py-3 font-mono">{r.weekly_calls}</td>
                <td className="px-4 py-3 font-mono">${r.avg_value}</td>
                <td className="px-4 py-3 font-mono">{r.miss_pct}%</td>
                <td className="px-4 py-3 text-right font-mono text-red-600">{fmt(r.revenue_lost)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-green-600">+{fmt(r.net_upside)}</td>
                <td className="px-4 py-3">
                  <span className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5">
                    {r.suggested_plan_name} · ${r.suggested_plan_price}/mo
                  </span>
                </td>
                <td className="px-4 py-3">
                  {r.conversation_id ? (
                    <Link
                      to={`/widget-chats?conv=${r.conversation_id}`}
                      data-testid={`roi-link-conv-${r.session_id.slice(0, 8)}`}
                      className="inline-flex items-center gap-1 text-xs text-neutral-700 hover:text-amber-600"
                    >
                      <MessageSquareText className="w-3 h-3" /> Chat <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : (
                    <span className="text-xs text-neutral-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-xs text-neutral-500">{timeAgo(r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  const colors = {
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    green: "border-green-200 bg-green-50 text-green-700",
    default: "border-neutral-200 bg-white text-neutral-900",
  };
  const cls = colors[accent] || colors.default;
  return (
    <div className={`border p-5 ${cls}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest font-mono opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}
